import { zipSync } from 'fflate';
import { INPUT_EXTENSIONS, type InputKind } from '../registry';

export interface OutputFile {
  name: string;
  bytes: Uint8Array;
  mime: string;
}

export const MIME = {
  pdf: 'application/pdf',
  zip: 'application/zip',
  txt: 'text/plain;charset=utf-8',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  jpg: 'image/jpeg',
  png: 'image/png',
} as const;

export function extension(name: string): string {
  const i = name.lastIndexOf('.');
  return i > 0 ? name.slice(i + 1).toLowerCase() : '';
}

export function baseName(name: string): string {
  const i = name.lastIndexOf('.');
  return i > 0 ? name.slice(0, i) : name;
}

/** File name safe for all operating systems. */
export function safeName(name: string): string {
  const cleaned = name
    .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 150);
  return cleaned || 'document';
}

/** e.g. ("Informe.pdf", "comprimit") → "Informe_comprimit.pdf" */
export function outputName(input: string, suffix: string, ext = 'pdf'): string {
  return safeName(`${baseName(input)}${suffix ? `_${suffix}` : ''}.${ext}`);
}

export function kindOf(name: string, mime: string): InputKind | null {
  const ext = extension(name);
  for (const [kind, exts] of Object.entries(INPUT_EXTENSIONS) as Array<[InputKind, string[]]>) {
    if (exts.includes(ext)) return kind;
  }
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('image/') && /jpeg|png|webp|gif|bmp/.test(mime)) return 'image';
  return null;
}

export function acceptAttribute(kinds: InputKind[]): string {
  const exts = kinds.flatMap((k) => INPUT_EXTENSIONS[k].map((e) => `.${e}`));
  const mimes = kinds.flatMap((k) => (k === 'pdf' ? ['application/pdf'] : k === 'image' ? ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp'] : []));
  return [...exts, ...mimes].join(',');
}

export function formatList(kinds: InputKind[]): string {
  return [...new Set(kinds.flatMap((k) => INPUT_EXTENSIONS[k].filter((e) => e !== 'jpeg').map((e) => e.toUpperCase())))].join(', ');
}

/** Makes names unique inside an archive: a.pdf, a (2).pdf… */
export function uniqueNames(names: string[]): string[] {
  const seen = new Map<string, number>();
  return names.map((n) => {
    const count = seen.get(n.toLowerCase()) ?? 0;
    seen.set(n.toLowerCase(), count + 1);
    return count === 0 ? n : `${baseName(n)} (${count + 1}).${extension(n)}`;
  });
}

export function zipFiles(files: OutputFile[], name: string): OutputFile {
  const names = uniqueNames(files.map((f) => f.name));
  const entries: Record<string, [Uint8Array, { level: 0 | 6 }]> = {};
  files.forEach((f, i) => {
    // PDFs and JPEGs are already compressed.
    const level = /pdf|jpeg|png|zip|docx/.test(f.mime) ? 0 : 6;
    entries[names[i]] = [f.bytes, { level }];
  });
  return { name: safeName(name), bytes: zipSync(entries), mime: MIME.zip };
}
